/**
 * COBOL Version Upgrade Executor
 *
 * Handles the 'cobol-version-upgrade' and 'pipeline-cobol-version-upgrade' node execution.
 * Calls the ModernizeIT API Layer /cobolversionupgrade endpoint.
 *
 * API Flow (WORKER MODE):
 * 1. Verify upstream idms_analysis and idms_compatibility jobs are 'completed'
 * 2. POST /cobolversionupgrade -> returns job_id with status QUEUED
 * 3. Poll GET /jobs/{job_id} -> wait until status = 'completed'
 * 4. Extract results from job.output_data
 *
 * CRITICAL: Must wait for both IDMS Analysis AND IDMS Compatibility to complete.
 */

import { pollJobUntilComplete } from '../../utils/jobPoller.js'
import { getAuthHeaders, handleApiError } from './authHelper.js'

/**
 * Execute the COBOL Version Upgrade node
 *
 * @param {Object} config - Node configuration
 * @param {Object} context - Workflow execution context
 * @param {Function} onProgress - Progress callback (progress: 0-100, message: string)
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Execution result with job info and artifacts
 */
export async function execute(config, context, onProgress = () => {}, signal = null) {
  const apiBaseUrl = context.api_base_url

  onProgress(0, 'Preparing COBOL Version Upgrade...')

  if (signal?.aborted) {
    throw new Error('Execution cancelled')
  }

  // 1. Verify upstream IDMS Analysis job exists
  const analysisJobId = context.jobs?.idms_analysis_job_id
  if (!analysisJobId) {
    throw new Error('COBOL Version Upgrade requires IDMS Analysis job ID. Run IDMS Analysis first.')
  }

  // 2. Verify upstream IDMS Compatibility job exists
  const compatibilityJobId = context.jobs?.idms_compatibility_job_id
  if (!compatibilityJobId) {
    throw new Error('COBOL Version Upgrade requires IDMS Compatibility job ID. Run IDMS Compatibility first.')
  }

  // 3. Verify upstream jobs are completed
  console.log(`[CobolVersionUpgradeExecutor] Verifying upstream jobs...`)
  onProgress(2, 'Waiting for upstream jobs to complete...')

  try {
    await pollJobUntilComplete(analysisJobId, apiBaseUrl, {
      maxWaitSeconds: 600,
      pollInterval: 1000,
      onProgress: (progress, message) => {
        const mappedProgress = 2 + (progress * 0.03)
        onProgress(mappedProgress, `Waiting for IDMS Analysis: ${message}`)
      },
      signal
    })
  } catch (error) {
    throw new Error(`Upstream IDMS Analysis job failed: ${error.message}`)
  }

  try {
    await pollJobUntilComplete(compatibilityJobId, apiBaseUrl, {
      maxWaitSeconds: 600,
      pollInterval: 1000,
      onProgress: (progress, message) => {
        const mappedProgress = 5 + (progress * 0.03)
        onProgress(mappedProgress, `Waiting for IDMS Compatibility: ${message}`)
      },
      signal
    })
  } catch (error) {
    throw new Error(`Upstream IDMS Compatibility job failed: ${error.message}`)
  }

  onProgress(8, 'Upstream jobs complete, starting version upgrade...')

  // 4. Build request body
  const requestBody = {
    scout_account_id: context.scout_account_id,
    application_name: context.application_name,
    user_id: context.user_id,
    idms_analysis_job_id: analysisJobId,
    idms_compatibility_job_id: compatibilityJobId,
  }

  onProgress(10, 'Submitting version upgrade job...')

  // 5. Submit the job
  let jobResponse
  try {
    const response = await fetch(`${apiBaseUrl}/cobolversionupgrade`, {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(requestBody),
      signal,
    })

    if (!response.ok) {
      await handleApiError(response)
    }

    jobResponse = await response.json()

    if (jobResponse.success === false) {
      throw new Error(jobResponse.error || 'COBOL Version Upgrade failed')
    }

  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Execution cancelled')
    }
    if (error.message.includes('Failed to fetch')) {
      throw new Error(`Cannot connect to API at ${apiBaseUrl}. Is the server running?`)
    }
    throw error
  }

  const jobId = jobResponse.job_id
  if (!jobId) {
    throw new Error('API did not return a job_id')
  }

  console.log(`[CobolVersionUpgradeExecutor] Job created: ${jobId}, polling...`)
  onProgress(15, `Job started: ${jobId}`)

  // 6. Poll for completion
  const completedJob = await pollJobUntilComplete(jobId, apiBaseUrl, {
    maxWaitSeconds: 600,
    pollInterval: 1000,
    onProgress: (progress, message) => {
      const mappedProgress = 15 + (progress * 0.80)
      onProgress(mappedProgress, message)
    },
    signal
  })

  console.log('[CobolVersionUpgradeExecutor] Job completed')
  onProgress(95, 'Extracting results...')

  const outputData = completedJob.output_data
  if (!outputData) {
    throw new Error('Job completed but has no output_data')
  }

  onProgress(100, 'COBOL Version Upgrade complete')

  return formatResult(jobId, outputData, context.jobs)
}

function formatResult(jobId, data, contextJobs = {}) {
  const resultJobs = {
    ...contextJobs,
    cobol_version_upgrade_job_id: jobId,
  }

  return {
    jobs: resultJobs,
    success: data.success !== false,
    status: data.status || 'completed',
    summary: data.summary || {},
    graph: data.graph || null,
    duration_ms: data.duration_ms,
    error: data.error || null,
  }
}

export default {
  execute,
}
